'use client';

import * as React from 'react';
import { Input } from '../components/input';
import { Eye, EyeSlash } from '../icons';
import { cn } from '../lib/cn';

/**
 * Password field with a show/hide toggle. On a phone keyboard, being able to
 * check what you typed beats a confirm field for sign in. The toggle is a real
 * 44px toggle button with a label, not an icon alone.
 */
function PasswordInput({ className, ...props }: Omit<React.ComponentProps<'input'>, 'type'>) {
  const [visible, setVisible] = React.useState(false);
  const Glyph = visible ? EyeSlash : Eye;

  return (
    <div data-slot="password-input" className="relative">
      <Input
        type={visible ? 'text' : 'password'}
        // Stops phones from "correcting" a typed password.
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className={cn('pr-12', className)}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible((value) => !value)}
        // A toggle keeps one label; aria-pressed says whether it is on.
        aria-label="Show password"
        aria-pressed={visible}
        disabled={props.disabled}
        className={cn(
          'absolute inset-y-0 right-0 flex w-(--spacing-touch) items-center justify-center rounded-r-(--radius-input) text-neutral-600',
          'outline-none hover:text-navy-900 disabled:pointer-events-none disabled:opacity-55 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-(--color-focus-light)',
        )}
      >
        <Glyph size={20} aria-hidden="true" />
      </button>
    </div>
  );
}

export { PasswordInput };
